// client/src/pages/CampsListPage.jsx
// Home for authed users. Lists the camps the user can access.
//   Super admin: every live camp + "+ תכנית חדשה" CTA.
//   Camp manager: only their camps; no create button.
//   Unassigned authed user: empty state with "פנה ל-Super Admin" copy.
//
// Camp cards rotate through six accent colours and surface a live/upcoming
// /past status pill so a glance tells you which camp matters today without
// reading the date strip.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCamps, useCreateCamp } from '../hooks/useCamps.js';
import Spinner from '../components/Spinner.jsx';
import Modal from '../components/Modal.jsx';
import CampForm from '../components/CampForm.jsx';
import { formatDate } from '../lib/format.js';
import { BuildingLibraryIcon } from '../components/Icons.jsx';

// One playful accent per card slot, in rotation. Each entry is a tuple of
// (strap class for the top border, soft tint for the icon tile, icon
// foreground colour) — spelled out in full so Tailwind's JIT picks them up.
const ACCENTS = [
  { strap: 'bg-brand-gradient', tile: 'bg-brand-100 text-brand-700' },
  { strap: 'bg-joy-gradient',   tile: 'bg-joy-100   text-joy-700'   },
  { strap: 'bg-mint-gradient',  tile: 'bg-mint-100  text-mint-700'  },
  { strap: 'bg-gold-gradient',  tile: 'bg-gold-100  text-gold-700'  },
  { strap: 'bg-sky-gradient',   tile: 'bg-sky-100   text-sky-700'   },
  { strap: 'bg-gradient-to-br from-clay-400 to-joy-500',
    tile:  'bg-clay-100  text-clay-700' },
];

// Below this many camps a search box is noise — everything is visible at a
// glance anyway.
const SEARCH_THRESHOLD = 6;

export default function CampsListPage({ user }) {
  const camps = useCamps();
  const [creating, setCreating] = useState(false);
  const [createDirty, setCreateDirty] = useState(false);
  const [query, setQuery] = useState('');
  const createMutation = useCreateCamp();

  if (camps.isLoading) return <Spinner label="טוען תכניות..." />;

  // Keep each card's accent stable under filtering by pinning it to the
  // camp's position in the FULL list, not the filtered one.
  const allCamps = (camps.data || []).map((camp, idx) => ({ camp, accent: ACCENTS[idx % ACCENTS.length] }));
  const q = query.trim().toLowerCase();
  const visibleCamps = q
    ? allCamps.filter(({ camp }) =>
        camp.name.toLowerCase().includes(q) ||
        (camp.description || '').toLowerCase().includes(q))
    : allCamps;
  const showSearch = allCamps.length >= SEARCH_THRESHOLD;

  return (
    <div className="space-y-6 sm:space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow">לוח הבית</p>
          <h1 className="h-page mt-1">תכניות</h1>
          <p className="mt-2 text-muted">
            {user.isSuperAdmin
              ? 'אתה רואה את כל התכניות במערכת.'
              : 'התכניות שאתה מנהל.'}
          </p>
        </div>
        {user.isSuperAdmin && (
          <button
            type="button"
            className="btn-pop btn-lg"
            onClick={() => setCreating(true)}
          >
            <PlusIcon className="w-5 h-5" />
            תכנית חדשה
          </button>
        )}
      </header>

      {camps.isError && (
        <div className="card-danger p-4 text-joy-900 text-sm">
          שגיאה בטעינה: {camps.error.message}
        </div>
      )}

      {camps.data?.length === 0 && (
        <EmptyState isSuperAdmin={user.isSuperAdmin} onCreate={() => setCreating(true)} />
      )}

      {showSearch && (
        <div className="relative max-w-sm">
          <SearchIcon className="w-4 h-4 text-ink-400 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
          <input
            type="search"
            className="input ps-9"
            placeholder="חיפוש תכנית..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="חיפוש תכנית"
          />
        </div>
      )}

      {visibleCamps.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleCamps.map(({ camp, accent }) => (
            <CampCard key={camp.id} camp={camp} accent={accent} />
          ))}
        </div>
      )}

      {allCamps.length > 0 && visibleCamps.length === 0 && (
        <p className="text-muted text-sm">
          לא נמצאו תכניות עבור "{query.trim()}".
        </p>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        isDirty={createDirty}
        title="תכנית חדשה"
      >
        <CampForm
          submitLabel="צור תכנית"
          onDirtyChange={setCreateDirty}
          onCancel={() => setCreating(false)}
          onSubmit={async (body) => {
            await createMutation.mutateAsync(body);
            setCreating(false);
          }}
        />
      </Modal>
    </div>
  );
}

/**
 * Buckets a camp into live / upcoming / past / open based on its dates so
 * the card can show one pill at a glance. Logic is intentionally inclusive
 * — a same-day end is still "live" until midnight.
 */
function campStatus(camp) {
  const now = Date.now();
  const start = camp.startDate ? new Date(camp.startDate).getTime() : null;
  const end   = camp.endDate   ? new Date(camp.endDate).getTime()   : null;
  if (start && now < start) return { label: 'עוד לא התחילה', tone: 'badge-slate' };
  if (end && now > end)     return { label: 'הסתיימה',       tone: 'badge-slate' };
  if (start && (!end || now <= end)) return { label: 'פעילה עכשיו', tone: 'badge-mint' };
  return { label: 'פתוחה', tone: 'badge-indigo' };
}

function CampCard({ camp, accent }) {
  const status = campStatus(camp);
  return (
    <Link
      to={`/camps/${camp.id}`}
      className="card hover:shadow-pop hover:-translate-y-0.5 hover:border-brand-200
                 transition-all block overflow-hidden group"
    >
      <div className={`h-1.5 ${accent.strap}`} />
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${accent.tile}`}>
            <BuildingLibraryIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-bold text-ink-900 truncate group-hover:text-brand-700 transition-colors">
                {camp.name}
              </h3>
              <span className={status.tone}>{status.label}</span>
            </div>
            {camp.description && (
              <p className="mt-1.5 text-sm text-ink-600 clamp-2">{camp.description}</p>
            )}
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-ink-100 flex items-center justify-between text-xs text-ink-500">
          {(camp.startDate || camp.endDate) ? (
            <span className="num">
              {camp.startDate ? formatDate(camp.startDate) : '—'}
              {' '}–{' '}
              {camp.endDate ? formatDate(camp.endDate) : 'ללא תאריך סיום'}
            </span>
          ) : (
            <span>ללא תאריכים</span>
          )}
          <span className="text-brand-600 font-semibold group-hover:translate-x-1 transition-transform">
            פתח ←
          </span>
        </div>
      </div>
    </Link>
  );
}

function EmptyState({ isSuperAdmin, onCreate }) {
  return (
    <div className="card-pop p-10 sm:p-14 text-center max-w-xl mx-auto">
      <div className="mx-auto w-16 h-16 rounded-3xl bg-brand-gradient text-white grid place-items-center mb-4 shadow-pop">
        <BuildingLibraryIcon className="w-8 h-8" />
      </div>
      <h2 className="text-lg font-bold text-ink-900">אין תכניות עדיין</h2>
      {isSuperAdmin ? (
        <>
          <p className="mt-2 text-muted">
            צור את התכנית הראשונה ומשם תוכל להוסיף תלמידים ומכשירים.
          </p>
          <button type="button" className="btn-pop btn-lg mt-5" onClick={onCreate}>
            <PlusIcon className="w-5 h-5" />
            צור תכנית ראשונה
          </button>
        </>
      ) : (
        <p className="mt-2 text-muted">
          עוד לא הוקצית לאף תכנית. פנה ל-Super Admin להוספה.
        </p>
      )}
    </div>
  );
}

function PlusIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function SearchIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
    </svg>
  );
}
